import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'shared/context/AuthContext';
import * as oauthApi from 'shared/api/auth/oauthApi';

/**
 * Страница обработки OAuth callback
 * URL: /oauth/callback?code=...&state=...&provider=...
 */
const OAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setTokens, loadUser } = useAuth();
  
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [error, setError] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const provider = searchParams.get('provider');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Проверка на ошибку от провайдера
      if (errorParam) {
        setStatus('error');
        setError(errorDescription || `Ошибка авторизации: ${errorParam}`);
        return;
      }

      // Проверка обязательных параметров
      if (!code) {
        setStatus('error');
        setError('Отсутствует код авторизации');
        return;
      }

      try {
        // Обмен code на токены через наш бэкенд
        const redirectUri = `${window.location.origin}/oauth/callback`;
        const tokens = await oauthApi.exchangeCode({
          code,
          state,
          provider,
          redirectUri,
        });

        // Сохраняем токены
        setTokens(tokens.accessToken, tokens.refreshToken);
        
        // Загружаем данные пользователя
        await loadUser();

        setStatus('success');

        // Редирект на сохранённый URL или на профиль
        const returnUrl = sessionStorage.getItem('oauth_return_url') || '/user-profile-settings';
        sessionStorage.removeItem('oauth_return_url');
        
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 1500);

      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        
        if (err.status === 401) {
          setError('Авторизация отклонена. Попробуйте снова.');
        } else if (err.status === 409) {
          setError('Пользователь с таким email уже существует. Войдите через email/пароль.');
        } else {
          setError(err.message || 'Ошибка авторизации. Попробуйте позже.');
        }
      }
    };

    handleCallback();
  }, [searchParams, setTokens, loadUser, navigate]);

  const handleRetry = () => {
    navigate('/login-screen', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center px-4">
      <div className="bg-surface rounded-xl shadow-lg border border-secondary-200 p-8 w-full max-w-md text-center">
        
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Loader2" size={32} className="text-primary animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Выполняется вход...
            </h1>
            <p className="text-text-secondary">
              Подождите, мы проверяем ваши данные
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Check" size={32} className="text-success" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Вход выполнен!
            </h1>
            <p className="text-text-secondary">
              Перенаправление...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="X" size={32} className="text-error" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary mb-2">
              Ошибка авторизации
            </h1>
            <p className="text-text-secondary mb-6">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Попробовать снова
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
